import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { session as defaultSession, type SessionStore } from './session';
import type { SessionState } from './types';
import { AuthContext, type AuthContextValue } from './useAuth';

/** Kto jest zalogowany i w jakim koncie — null, gdy nikt. */
function identityOf(state: SessionState): string | null {
  return state.status === 'authenticated' ? `${state.profile.userId}:${state.profile.account.id}` : null;
}

export function AuthProvider({ children, store = defaultSession }: { children: ReactNode; store?: SessionStore }) {
  // na serwerze (SSR) sesja zawsze „wczytywana” — odświeżenie z ciasteczka robi dopiero przeglądarka
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (store.getState().status === 'loading') void store.refresh();
  }, [store]);

  // Dane z pamięci podręcznej nie mogą przejść na inną sesję: czyścimy je przy wylogowaniu (także wygaśnięciu sesji),
  // po zalogowaniu i przy zmianie użytkownika albo konta. Odświeżenie tokenu tej samej sesji ich nie rusza.
  useEffect(() => {
    let previous = store.getState();
    return store.subscribe((next) => {
      const before = identityOf(previous);
      const after = identityOf(next);
      const loggedIn = previous.status === 'anonymous' && after != null;
      if ((before != null && after !== before) || loggedIn) queryClient.clear();
      previous = next;
    });
  }, [store, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      loginWithPassword: (email, password) => store.login('/api/auth/login', { email, password }),
      verifyEmail: (token) => store.login('/api/auth/verify-email', { token }),
      loginWithGoogle: (credential, signup) =>
        store.login('/api/auth/google', signup ? { credential, signup } : { credential }),
      switchAccount: (accountId) => store.login('/api/auth/switch', { accountId }),
      acceptInvitation: (token) => store.login('/api/auth/invitations/accept', { token }),
      registerFromInvitation: (token, name, password) =>
        store.login('/api/auth/invitations/register', { token, name, password, termsAccepted: true }),
      logout: () => store.logout(),
      endSession: (reason) => {
        store.clear(reason);
      },
      updateProfile: (profile) => {
        store.updateProfile(profile);
      },
    }),
    [state, store],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
